import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, TrendingUp, Zap, Shield, Check, ArrowRight, Play, Menu, X,
  Mail, Phone, MessageSquare, Target, Activity, Calendar, Sparkles,
  MapPin, BarChart3, MousePointer, Bot, LineChart, Eye, Send, Clock,
  Rocket, Globe, Lock, ChevronRight
} from 'lucide-react';
import { supabase } from '../config/supabase';

// 60/30/10 Color Theme: 60% Blue, 30% White, 10% Light Blue
const LandingPage = ({ onGetStarted }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({ totalLeads: 0, totalCampaigns: 0, totalEmails: 0, activeUsers: 0 });

  useEffect(() => {
    const fetchPublicStats = async () => {
      try {
        const { count: leadsCount } = await supabase.from('leads').select('*', { count: 'exact', head: true });
        const { data: campaignsData } = await supabase.from('leads').select('campaign_name');
        const uniqueCampaigns = new Set(campaignsData?.map(l => l.campaign_name).filter(Boolean) || []);
        const { count: emailsCount } = await supabase.from('lead_events').select('*', { count: 'exact', head: true }).in('event_type', ['sent', 'email_sent']);
        setStats({ totalLeads: leadsCount || 0, totalCampaigns: uniqueCampaigns.size || 0, totalEmails: emailsCount || 0, activeUsers: Math.max(3, Math.floor((leadsCount || 0) / 500)) });
      } catch (e) {
        setStats({ totalLeads: 1000, totalCampaigns: 5, totalEmails: 5000, activeUsers: 5 });
      }
    };
    fetchPublicStats();
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const features = [
    { icon: Bot, title: "Smart Email Automation", description: "Multi-step sequences with intelligent timing and reply detection." },
    { icon: LineChart, title: "AI Lead Scoring", description: "Automatic lead prioritization based on engagement signals." },
    { icon: Target, title: "Campaign Analytics", description: "Real-time dashboards with open rates and conversion insights." },
    { icon: Phone, title: "Smart Call Lists", description: "Auto-generated queues prioritizing your hottest leads." }
  ];

  const capabilities = [
    { icon: Eye, text: "Open Tracking" }, { icon: MousePointer, text: "Click Analytics" },
    { icon: MessageSquare, text: "Reply Detection" }, { icon: Calendar, text: "Meeting Scheduler" },
    { icon: Shield, text: "Role-Based Access" }, { icon: BarChart3, text: "Live Dashboard" },
    { icon: Clock, text: "Timezone Smart" }, { icon: Lock, text: "Secure & Private" }
  ];

  return (
    <div className="min-h-screen bg-blue-600 overflow-hidden">
      {/* Subtle Background - No heavy blur */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-blue-500/30 rounded-full blur-2xl transform translate-x-1/4 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-blue-700/20 rounded-full blur-2xl transform -translate-x-1/4 translate-y-1/4" />
      </div>

      {/* Navigation - Clean White */}
      <nav className="fixed top-0 w-full z-50 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg shadow-blue-900/10 px-6 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center overflow-hidden">
                  <img src="/Creative Gorilla logo.jpeg" alt="Logo" className="w-7 h-7 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div>
                  <span className="text-xl font-bold text-blue-900">CREATILLA</span>
                  <p className="text-[10px] text-blue-500 -mt-0.5">A Product of Creative Gorilla</p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="#features" className="text-blue-700 hover:text-blue-900 transition-colors text-sm font-medium">Features</a>
                <a href="#stats" className="text-blue-700 hover:text-blue-900 transition-colors text-sm font-medium">Stats</a>
                <a href="#contact" className="text-blue-700 hover:text-blue-900 transition-colors text-sm font-medium">Contact</a>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onGetStarted} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow-lg shadow-blue-600/30 transition-all">
                  Get Started
                </motion.button>
              </div>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-blue-700 hover:bg-blue-50 rounded-lg transition-colors">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="md:hidden mt-2 max-w-7xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-4 space-y-2">
                <a href="#features" className="block text-blue-700 hover:bg-blue-50 py-2 px-4 rounded-xl font-medium">Features</a>
                <a href="#stats" className="block text-blue-700 hover:bg-blue-50 py-2 px-4 rounded-xl font-medium">Stats</a>
                <a href="#contact" className="block text-blue-700 hover:bg-blue-50 py-2 px-4 rounded-xl font-medium">Contact</a>
                <button onClick={onGetStarted} className="w-full bg-blue-600 text-white px-5 py-3 rounded-xl font-semibold">Get Started</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center space-x-2 bg-white/15 border border-white/25 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-sky-200" />
                <span className="text-white text-sm font-medium">Next-Gen Sales Automation</span>
              </motion.div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-white">Automate Your</span><br />
                <span className="text-sky-200">Sales Pipeline</span>
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-lg">
                Intelligent CRM with email automation, AI-powered lead scoring, and real-time campaign analytics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.button whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }} onClick={onGetStarted} className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold text-lg shadow-xl shadow-blue-900/20 hover:shadow-2xl transition-all flex items-center justify-center space-x-2 group">
                  <span>Start Free</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="bg-white/15 border border-white/30 text-white hover:bg-white/25 px-8 py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center space-x-2">
                  <Play className="w-5 h-5" /><span>Watch Demo</span>
                </motion.button>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-sky-200" />
                  <span className="text-sm text-white/70">Enterprise Security</span>
                </div>
                <div className="h-4 w-px bg-white/20" />
                <div className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-sky-200" />
                  <span className="text-sm text-white/70">GDPR Compliant</span>
                </div>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="bg-white rounded-3xl p-6 shadow-2xl shadow-blue-900/20">
                <div className="flex items-center space-x-2 mb-5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <div className="flex-1 mx-4 bg-blue-50 rounded-lg h-6"></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-blue-50 rounded-2xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-blue-600/70 font-medium">Active Leads</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{formatNumber(stats.totalLeads)}</p>
                    <p className="text-xs text-green-600 flex items-center mt-1"><TrendingUp className="w-3 h-3 mr-1" />Growing</p>
                  </div>
                  <div className="bg-blue-50 rounded-2xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Target className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-blue-600/70 font-medium">Campaigns</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{stats.totalCampaigns}</p>
                    <p className="text-xs text-blue-500 flex items-center mt-1"><Activity className="w-3 h-3 mr-1" />Active</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-sky-50 rounded-2xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Send className="w-4 h-4 text-sky-600" />
                      <span className="text-xs text-sky-600/70 font-medium">Emails Sent</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{formatNumber(stats.totalEmails)}</p>
                  </div>
                  <div className="bg-sky-50 rounded-2xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Users className="w-4 h-4 text-sky-600" />
                      <span className="text-xs text-sky-600/70 font-medium">Team</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{stats.activeUsers}</p>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-900">Live Activity</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-green-600 font-medium">Live</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[{ icon: Mail, text: "Email opened", color: "bg-blue-100 text-blue-600" }, { icon: MessageSquare, text: "New reply", color: "bg-green-100 text-green-600" }, { icon: Eye, text: "Link clicked", color: "bg-sky-100 text-sky-600" }].map((item, i) => (
                      <div key={i} className="flex items-center space-x-3 p-2 bg-white rounded-xl">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                          <item.icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900">{item.text}</p>
                          <p className="text-xs text-blue-400">Just now</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <motion.div animate={{ y: [-6, 6, -6] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-3 -right-3 bg-green-500 rounded-xl p-3 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white">Sent!</span>
                </div>
              </motion.div>
              <motion.div animate={{ y: [6, -6, 6] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute -bottom-3 -left-3 bg-sky-400 rounded-xl p-3 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Zap className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white">Scored!</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Matches header width */}
      <section id="stats" className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/10 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[{ value: formatNumber(stats.totalLeads), label: "Leads Managed", icon: Users }, { value: stats.totalCampaigns, label: "Campaigns", icon: Target }, { value: formatNumber(stats.totalEmails), label: "Emails Sent", icon: Mail }, { value: "99.9%", label: "Uptime", icon: Shield }].map((stat, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-100 flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-3xl font-bold text-blue-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-blue-500">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/15 border border-white/25 rounded-full px-4 py-2 mb-4">
              <Rocket className="w-4 h-4 text-sky-200" />
              <span className="text-white text-sm font-medium">Core Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need to <span className="text-sky-200">Close More Deals</span>
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">Powerful automation tools designed for modern sales teams.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} whileHover={{ y: -4 }} className="bg-white rounded-2xl p-6 shadow-lg shadow-blue-900/10 hover:shadow-xl transition-all group">
                  <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{feature.title}</h3>
                  <p className="text-blue-600/70 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {capabilities.map((cap, index) => {
              const Icon = cap.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }} className="bg-white/15 border border-white/25 rounded-xl p-4 text-center hover:bg-white/25 transition-colors">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-sky-200" />
                  </div>
                  <p className="text-sm font-medium text-white">{cap.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-10 text-center shadow-2xl shadow-blue-900/20">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Ready to Transform Your Sales?</h2>
              <p className="text-lg text-blue-600/70 mb-8 max-w-xl mx-auto">Join teams using CREATILLA to automate outreach and close more deals.</p>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onGetStarted} className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-lg shadow-blue-600/30 transition-all inline-flex items-center space-x-2">
                <span>Get Started Free</span>
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer - Enhanced Visibility */}
      <footer id="contact" className="py-16 px-4 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center overflow-hidden shadow-lg shadow-blue-600/30">
                  <img src="/Creative Gorilla logo.jpeg" alt="Logo" className="w-11 h-11 rounded-xl object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div>
                  <span className="text-3xl font-bold text-blue-900">CREATILLA</span>
                  <p className="text-base text-blue-600 font-semibold">A Product of Creative Gorilla</p>
                </div>
              </div>
              <p className="text-gray-700 text-base leading-relaxed max-w-md">Next-generation sales automation platform with intelligent email sequences, AI lead scoring, and real-time analytics.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-4">Developer</h4>
              <p className="text-gray-900 font-bold text-lg mb-2">MD Ai.MAN ALI SHEZAN</p>
              <div className="flex items-center space-x-2 text-gray-700 text-base mb-3">
                <MapPin className="w-4 h-4 text-blue-600" /><span>Thakurgoan, Bangladesh</span>
              </div>
              <a href="mailto:aimanalishezanbusiness@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium text-base transition-colors underline decoration-blue-300 hover:decoration-blue-600">
                aimanalishezanbusiness@gmail.com
              </a>
            </div>
            <div>
              <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-4">CEO</h4>
              <p className="text-gray-900 font-bold text-lg mb-2">JANI NIEMI</p>
              <div className="flex items-center space-x-2 text-gray-700 text-base mb-3">
                <MapPin className="w-4 h-4 text-blue-600" /><span>Lahti, Finland</span>
              </div>
              <a href="mailto:jani@otherwise.fi" className="text-blue-600 hover:text-blue-800 font-medium text-base transition-colors underline decoration-blue-300 hover:decoration-blue-600">
                jani@otherwise.fi
              </a>
            </div>
          </div>
          <div className="border-t-2 border-blue-200 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-600 text-base font-medium">© 2024 <span className="text-blue-700 font-bold">CREATILLA</span>. All rights reserved.</p>
            <div className="flex space-x-8">
              <a href="#" className="text-gray-600 hover:text-blue-700 text-base font-medium transition-colors">Privacy</a>
              <a href="#" className="text-gray-600 hover:text-blue-700 text-base font-medium transition-colors">Terms</a>
              <a href="#" className="text-gray-600 hover:text-blue-700 text-base font-medium transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
